import { LeftOutlined, PlusOutlined, ReloadOutlined, RightOutlined } from '@ant-design/icons';
import { Alert, Button, Card, message, Segmented, Select, Spin, Tooltip, Typography } from 'antd';
import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import dayjs, { type Dayjs } from 'dayjs';
import { useScreen } from 'hooks/useScreen';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { queryErrorMessage } from '../CashTransactions/types';
import EntryDrawer from './EntryDrawer';
import MonthView from './MonthView';
import {
  type CalendarEntry,
  type CalendarKind,
  type CalendarSeason,
  covers,
  DATE,
  type EntryDraft,
  isWeekend,
  monthGrid,
  monthName,
  seasonWindowOf,
} from './types';
import YearView from './YearView';

const { Title, Text } = Typography;

type View = 'month' | 'year';

const VIEW_KEY = 'calendar.view';

const readView = (): View => {
  try {
    return localStorage.getItem(VIEW_KEY) === 'year' ? 'year' : 'month';
  } catch {
    return 'month';
  }
};

// The calendar Screen (CONTEXT.md: Calendar entry, Holiday): a month or a
// whole season (1 September – 31 August) of entries.
export default function Calendar() {
  const { level, runQuery } = useScreen();
  const canEdit = level === 'edit';

  const [seasons, setSeasons] = useState<CalendarSeason[]>([]);
  const [kinds, setKinds] = useState<CalendarKind[]>([]);
  const [entries, setEntries] = useState<CalendarEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<View>(readView);
  // The month shown; in the year view, a month of the season shown.
  const [cursor, setCursor] = useState<Dayjs>(() => dayjs().startOf('month'));
  const [draft, setDraft] = useState<EntryDraft | null>(null);

  const changeView = (v: View) => {
    setView(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      // Not remembered; fine.
    }
  };

  useEffect(() => {
    Promise.all([
      runQuery<CalendarSeason>('calendar_seasons', {}),
      runQuery<CalendarKind>('calendar_kinds', {}),
    ])
      .then(([s, k]) => {
        setSeasons(s);
        setKinds(k);
      })
      .catch((e) => message.error(queryErrorMessage(e)));
  }, [runQuery]);

  const schoolYear = useMemo(() => seasonWindowOf(cursor), [cursor]);
  const season = seasons.find((s) => s.start_year === schoolYear.startYear);

  // The dates whose entries are loaded: the 6 weeks shown, or the season.
  const range = useMemo(() => {
    if (view === 'year')
      return { from: schoolYear.from.format(DATE), to: schoolYear.to.format(DATE) };
    const grid = monthGrid(cursor);
    return { from: grid[0][0].format(DATE), to: grid[5][6].format(DATE) };
  }, [view, cursor, schoolYear]);

  const load = useCallback(() => {
    setLoading(true);
    runQuery<CalendarEntry>('calendar_entries', range)
      .then(setEntries)
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [runQuery, range]);

  useEffect(() => {
    load();
  }, [load]);

  const kindById = useMemo(() => new Map(kinds.map((k) => [k.id, k])), [kinds]);
  const terms = useMemo(() => seasons.flatMap((s) => s.terms), [seasons]);

  const step = (dir: 1 | -1) => setCursor((c) => c.add(dir * (view === 'year' ? 12 : 1), 'month'));
  const goToday = () => setCursor(dayjs().startOf('month'));
  const pickSeason = (id: number) => {
    const s = seasons.find((x) => x.id === id);
    if (!s) return;
    const today = dayjs().format(DATE);
    setCursor((s.from <= today && today <= s.to ? dayjs() : dayjs(s.from)).startOf('month'));
  };

  const openCreate = useCallback(
    (startDate: string, endDate: string) => canEdit && setDraft({ startDate, endDate }),
    [canEdit],
  );
  const openEntry = useCallback((entry: CalendarEntry) => setDraft({ entry }), []);

  // Weekdays of the season with no lessons — the Holidays that matter.
  const holidayWeekdays = useMemo(() => {
    if (view !== 'year') return null;
    const holidays = entries.filter((e) => kindById.get(e.kind_id)?.no_lessons);
    let count = 0;
    for (let d = schoolYear.from; !d.isAfter(schoolYear.to); d = d.add(1, 'day')) {
      const date = d.format(DATE);
      if (!isWeekend(d) && holidays.some((e) => covers(e, date))) count++;
    }
    return count;
  }, [view, entries, kindById, schoolYear]);

  const heading =
    view === 'year'
      ? `${schoolYear.startYear}–${schoolYear.startYear + 1} хичээлийн жил`
      : `${cursor.year()} оны ${monthName(cursor)}`;

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Card styles={{ body: { padding: 0 } }}>
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-4 py-3 lg:px-5">
          {canEdit && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={kinds.length === 0}
              onClick={() => {
                const today = dayjs().format(DATE);
                const day =
                  view === 'month' && cursor.isSame(dayjs(), 'month') ? today : cursor.format(DATE);
                openCreate(day, day);
              }}
            >
              Нэмэх
            </Button>
          )}
          <Button onClick={goToday}>Өнөөдөр</Button>
          <div className="flex">
            <Tooltip title={view === 'year' ? 'Өмнөх жил' : 'Өмнөх сар'}>
              <Button type="text" shape="circle" icon={<LeftOutlined />} onClick={() => step(-1)} />
            </Tooltip>
            <Tooltip title={view === 'year' ? 'Дараах жил' : 'Дараах сар'}>
              <Button type="text" shape="circle" icon={<RightOutlined />} onClick={() => step(1)} />
            </Tooltip>
          </div>
          <Title level={4} style={{ margin: 0 }} className="min-w-0 flex-1 truncate font-normal!">
            {heading}
          </Title>
          {loading && <Spin size="small" />}
          <Select
            className="w-44"
            placeholder="Хичээлийн жил"
            value={season?.id}
            onChange={pickSeason}
            options={seasons.map((s) => ({ value: s.id, label: s.name }))}
          />
          <Segmented<View>
            value={view}
            onChange={changeView}
            options={[
              { value: 'month', label: 'Сар' },
              { value: 'year', label: 'Жил' },
            ]}
          />
          <Tooltip title="Шинэчлэх">
            <Button icon={<ReloadOutlined />} onClick={load} />
          </Tooltip>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 text-xs text-gray-600 lg:px-5">
          {kinds.map((k) => (
            <span key={k.id} className="flex items-center gap-1.5">
              <span className="size-2.5" style={{ backgroundColor: k.color }} />
              {k.name}
              {k.no_lessons && <span className="text-gray-400">(хичээлгүй)</span>}
            </span>
          ))}
          <span className="flex items-center gap-1.5">
            <span className="size-2.5 border border-gray-200 bg-gray-100" />
            Улирлын гадна
          </span>
          {holidayWeekdays !== null && (
            <Text type="secondary" className="ml-auto text-xs">
              Хичээлгүй ажлын өдөр: <b className="text-gray-700">{holidayWeekdays}</b>
            </Text>
          )}
        </div>

        <div className="px-4 pb-5 lg:px-5">
          {kinds.length === 0 && (
            <Alert
              className="mb-3"
              type="info"
              showIcon
              message="Хуанлийн төрөл бүртгэгдээгүй байна. «Хуанлийн төрөл» цэснээс нэмнэ үү."
            />
          )}
          {!season && seasons.length > 0 && (
            <Alert
              className="mb-3"
              type="warning"
              showIcon
              message={`${schoolYear.startYear}–${schoolYear.startYear + 1} хичээлийн жил бүртгэгдээгүй тул энэ хугацаанд нэмэх боломжгүй.`}
            />
          )}
          {view === 'month' ? (
            <MonthView
              month={cursor}
              entries={entries}
              kinds={kindById}
              terms={terms}
              canEdit={canEdit && kinds.length > 0}
              onCreate={openCreate}
              onOpen={canEdit ? openEntry : undefined}
            />
          ) : (
            <YearView
              from={schoolYear.from}
              entries={entries}
              kinds={kindById}
              terms={terms}
              onPickMonth={(m) => {
                setCursor(m.startOf('month'));
                changeView('month');
              }}
            />
          )}
        </div>
      </Card>

      <EntryDrawer
        draft={draft}
        kinds={kinds}
        onClose={() => setDraft(null)}
        onSaved={() => {
          setDraft(null);
          load();
        }}
      />
    </LayoutWithMenu>
  );
}
